import { DocsArea, docsAreas } from '../components/docsLink/ReadTheDocsLink'

/** All valid app management area sub-paths. */
export const appManagementAreas = [
  'licenses',
  'settings',
  'settings/general',
  'settings/mongo',
  'settings/super-admin',
  'metrics',
  'collections',
  'devices',
  'connect',
  'connect/sdk',
  'connect/https',
  'access/api-keys',
  'access/api-keys/new',
  'access/license-tokens',
  'access/authentication',
  'cdc',
  'cdc/data-bridges',
  'cdc/data-bridges/new',
] as const

export type AppManagementArea = (typeof appManagementAreas)[number]
export type OrganizationManagementArea =
  | 'databases'
  | 'activity'
  | 'settings'
  | 'new-database'
  | 'access'
  | 'access/members'
  | 'access/roles'
  | 'access/access-grants'
export type UserTenantArea =
  | 'apps'
  | 'databases'
  | 'organizations'
  | 'settings'
  | 'apps/request-access'
  | 'databases/request-access'
  | 'employee-groups'
export type OrganizationSettingsArea = 'general' | 'super-admin'
export type OrganizationAccessArea =
  | 'members'
  | 'roles'
  | 'roles/new'
  | 'access-grants'
  | 'access-grants/new'
export type EmployeeGroupManagementArea = 'customers' | 'employees' | 'settings'

const DOCS_BASE_URL = 'https://docs.ditto.live'

type AppManagementParams = {
  organizationSlug?: string
  tenantId?: string
  appSlug: string
  // Required: there is no safe default. The product landing area is
  // 'collections', and an implicit default here would silently disagree with
  // the routers that own it. Every caller must choose deliberately.
  area: AppManagementArea
}

const link = {
  app: ({
    organizationSlug,
    tenantId,
    appSlug,
  }: {
    organizationSlug?: string
    tenantId?: string
    appSlug: string
  }): string => {
    if (organizationSlug) {
      return `/${organizationSlug}/${appSlug}`
    }
    if (tenantId) {
      // for admins
      return `/u/${tenantId}/${appSlug}`
    }

    // for regular users
    return `/database/${appSlug}`
  },
  appManagement: ({
    organizationSlug,
    appSlug,
    tenantId,
    area,
  }: AppManagementParams): string => {
    if (organizationSlug) {
      return `/${organizationSlug}/${appSlug}/${area}`
    }
    if (tenantId) {
      // for admins
      return `/u/${tenantId}/${appSlug}/${area}`
    }

    // for regular users
    return `/database/${appSlug}/${area}`
  },
  createOrganization: (): string => '/organizations/create',
  createApp: ({ userId }: { userId?: string }): string =>
    userId ? `/u/${userId}/new-database` : '/new-database',
  gettingStarted: (): string => '/',
  organizationManagement: (params: {
    slug: string
    area?: OrganizationManagementArea
  }): string => {
    return `/${params.slug}/${params.area || 'databases'}`
  },
  organizationSettings: (params: {
    slug: string
    area?: OrganizationSettingsArea
  }): string =>
    `/${params.slug}/settings${params.area ? '/' + params.area : ''}`,
  organizationAccess: (params: {
    slug: string
    area?: OrganizationAccessArea
  }): string => `/${params.slug}/access/${params.area || 'members'}`,
  editOrganizationRole: ({ name, slug }: { name: string; slug: string }) =>
    link.organizationAccess({ slug, area: 'roles' }) + `/${name}`,
  createOrganizationApp: (slug: string): string => `/${slug}/new-database`,
  findEmployeeGroup: () => '/find-employee-group',
  forge: ({
    databaseId,
    appId,
  }: {
    databaseId: string
    appId?: string
  }): string =>
    `/forge/${databaseId}${appId ? `/builder/${appId}/design` : ''}`,
  createEmployeeGroup: () => '/employee-groups/new',
  employeeGroupManagement: (params: {
    slug: string
    area?: EmployeeGroupManagementArea
  }) => `/employee-groups/${params.slug}/${params.area || 'customers'}`,
  appCDC: (params: Omit<AppManagementParams, 'area'>) =>
    link.appManagement({ area: 'cdc', ...params }),
  // TODO(2367): ID in URL sort of breaks our existing patterns, but we don't store a slug
  // or a required, friendly identifier for streams. We should consider changing this.
  appDataBridge: ({
    id,
    ...params
  }: Omit<AppManagementParams, 'area'> & { id: string }) =>
    link
      .appManagement({ area: 'cdc/data-bridges', ...params })
      .concat(`/${id}`),
  createAppDataBridge: (params: Omit<AppManagementParams, 'area'>) =>
    link.appManagement({ area: 'cdc/data-bridges/new', ...params }),
  findOrganization: (): string => '/find-organization',
  settings: (): string => '/settings',
  users: (): string => '/users',
  findApp: (): string => '/find-database',
  feedback: (): string => '/feedback',
  dqlEditor: ({ appId, dql }: { appId: string; dql?: string }): string =>
    `/dql-editor/${appId}${dql ? `?dql=${encodeURIComponent(dql)}` : ''}`,
  deviceDashboard: ({
    appSlug,
    organizationSlug,
    peerKey,
    userId,
  }: {
    appSlug: string
    organizationSlug?: string
    peerKey?: string
    userId?: string
  }) =>
    `${link.appManagement({
      appSlug,
      organizationSlug,
      tenantId: userId,
      area: 'devices',
    })}${peerKey ? `/${peerKey}` : ''}`,
  userApp: ({ userId, appSlug }: { userId: string; appSlug: string }): string =>
    `/u/${userId}/${appSlug}`,
  userApps: ({ userId }: { userId?: string }): string =>
    userId ? `/u/${userId}/databases` : '/databases',
  userTenant: ({ userId, area }: { userId?: string; area: UserTenantArea }) =>
    userId ? `/u/${userId}/${area}` : `/${area}`,
  userOrganizations: ({ userId }: { userId?: string }): string =>
    userId ? `/u/${userId}/organizations` : '/organizations',
  dittoDocs: (area?: DocsArea) =>
    `${DOCS_BASE_URL}/${area ? docsAreas[area] || '' : ''}`,
  support: () => 'https://support.ditto.com',
  notifications: () => '/notifications',
} as const

export default link

/** Returns true if the path is a 'static path' that doesn't cause a redirect
 *  when the tenant context changes.
 */
export function isStaticPath(path: string): boolean {
  const staticPaths = [
    link.findOrganization(),
    link.users(),
    link.findApp(),
    link.feedback(),
    link.support(),
    link.notifications(),
  ]

  return staticPaths.includes(path)
}
